/**
 * A small labelled chip. Every state colour in the console comes from here so that "hot" is
 * the same red on the Inbox row, the Kanban card and the handoff queue.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { Tier } from '../lib/types.ts';

export type PillTone = 'neutral' | 'brand' | 'ok' | 'warn' | 'danger' | 'info' | 'hot' | 'warm' | 'cold';

const TONES: Record<PillTone, string> = {
  neutral: 'bg-neutral-soft text-neutral-soft-fg',
  brand: 'bg-brand-soft text-brand-soft-fg',
  ok: 'bg-ok-soft text-ok-soft-fg',
  warn: 'bg-warn-soft text-warn-soft-fg',
  danger: 'bg-danger-soft text-danger-soft-fg',
  info: 'bg-info-soft text-info-soft-fg',
  hot: 'bg-danger-soft text-tier-hot-fg',
  warm: 'bg-warn-soft text-tier-warm-fg',
  cold: 'bg-neutral-soft text-tier-cold-fg',
};

export interface PillProps {
  children: ReactNode;
  tone?: PillTone;
  /** A leading dot, for state pills where colour alone would be the only signal. */
  dot?: boolean;
  title?: string;
  className?: string;
}

export function Pill({ children, tone = 'neutral', dot = false, title, className }: PillProps): ReactNode {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium leading-5 whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current opacity-80" />}
      {children}
    </span>
  );
}

export function TierPill({ tier, score }: { tier: Tier; score?: number }): ReactNode {
  const label = tier === 'hot' ? 'Hot' : tier === 'warm' ? 'Warm' : 'Cold';
  return (
    <Pill tone={tier} dot title={`Lead tier from scoring mc-1.0 — hot ≥ 70, warm ≥ 40`}>
      {score === undefined ? label : `${label} ${score}`}
    </Pill>
  );
}
